import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject } from '@angular/core';
import { Observable } from 'rxjs';
import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { FormControl, Validators } from '@angular/forms';
import { DatePipe } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { exist } from '@campaign-portal/namespace/common/id';
import { RPCResult } from '@campaign-portal/namespace/common/rpc.response';
import { Campaign, CampaignStatus } from '@campaign-portal/namespace/entities/campaigns/specs';
import {
	AlarisDialogComponent,
	AlarisDialogService,
	AlarisIconDirective,
	AlarisNoValueCheckPipe,
	AlarisTextAreaComponent,
	DateFormatPipe,
	InputError,
} from '@campaign-portal/components-library';
import { CampaignsService } from '../../services/campaigns.service';
import { BaseActionDialogComponent, CanDeactivateGuardService } from '@shared/can-deactivate';

const CAMPAIGN_DESCRIPTION_MAX_LENGTH = 170;

export interface CampaignsDialogData {
	type: CampaignsDialogType;
	campaign: Campaign<exist>;
	total?: number;
}

export type CampaignsDialogType = 'Delete' | 'Confirm';

@Component({
	selector: 'app-campaigns-dialog',
	templateUrl: './campaigns-dialog.component.html',
	styleUrls: ['../../../../../styles/dialog.scss', './shared-dialog.component.scss'],
	providers: [DatePipe],
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [
		TranslateModule,
		AlarisTextAreaComponent,
		AlarisDialogComponent,
		AlarisNoValueCheckPipe,
		AlarisIconDirective,
		DateFormatPipe,
	],
	standalone: true,
})
export class CampaignsDialogComponent extends BaseActionDialogComponent {
	readonly campaignStatus = CampaignStatus;
	campaign!: Campaign<exist>;
	description: FormControl;
	total = 0;

	readonly errors: InputError[] = [{ key: 'maxlength', value: 'errors.maxCharacters', params: { amount: '170' } }];

	constructor(
		@Inject(DIALOG_DATA) public readonly data: CampaignsDialogData,
		private readonly dialogRef: DialogRef<unknown>,
		private readonly campaignsService: CampaignsService,
		private readonly translate: TranslateService,
		private readonly datePipe: DatePipe,
		private readonly cd: ChangeDetectorRef,
		private readonly dialog: AlarisDialogService,
		private readonly guard: CanDeactivateGuardService
	) {
		super();
		this.addDialogGuard(this.dialog, dialogRef, this.guard);
		this.campaign = data.campaign;
		this.description = new FormControl(
			this.campaign.info?.description || '',
			Validators.maxLength(CAMPAIGN_DESCRIPTION_MAX_LENGTH)
		);
		this.total = data?.total || 0;
	}

	get disabled(): boolean {
		return this.loading$.getValue();
	}

	close(): void {
		this.dialogRef.close(false);
	}

	confirm(): void {
		let caller$: Observable<RPCResult<unknown>> | null = null;

		switch (this.data.type) {
			case 'Delete':
				caller$ = this.campaignsService.delete(this.campaign);
				break;
			case 'Confirm':
				this.campaign.info.description = this.description.value ?? '';
				this.campaign.info.name = this.campaign.info.name
					? this.campaign.info.name
					: this.translate.instant('campaigns.campaign') +
						' ' +
						this.datePipe.transform(this.campaign.info.scheduled, 'd MMM y, HH:mm');
				caller$ = this.campaignsService.create(this.campaign);
				break;
			default:
				break;
		}

		this.runAction(caller$, (resp) => {
			this.dialogRef.close(resp.Success);
			this.cd.markForCheck();
		});
	}
}
